/**
 * azure-functions/shared/workflows/workflowEvidence.cjs
 *
 * PDWB-03 — what workflow runs mean to the rest of the management system,
 * read from ONE input: `state.workflowEvidence`, the read-only overlay
 * shared/state/assemble.js attaches from the workflow tables
 * (store.readWorkflowEvidence). One copy, shared by the browser and the
 * Function App, like runAssurance.cjs: the compliance engine's gate 4, the
 * obligation criterion `workflow-run-current`, the obligation task's workflow
 * annotation, and a risk treatment's workflow backing (RISK-B-006) all decide
 * through the one countability rule `isRunCountable` — never a second rule
 * (docs/workflow-engine-design.md §3e, §3g, §3n).
 *
 * Three rules every consumer inherits from here (PDWB-03 co-owner):
 *
 *   1. ATTRIBUTION THROUGH THE FROZEN LINK. A run is evidence for a control,
 *      obligation or risk only when the version it RAN linked that target
 *      (run.definitionSnapshot.links) AND the definition's current published
 *      version still links it. A link added by a later version claims only
 *      later runs (WF-B-004).
 *   2. ONE WINDOW RULE. A definition with no cadence (a manual trigger, or a
 *      schedule that borrows a cadence from no linked obligation) is never
 *      countable and never overdue, for every consumer. The latest JUDGED run
 *      decides — a newer failure is never hidden behind an older pass; a
 *      cancelled run judged nothing and is skipped.
 *   3. WHAT IS NOT KNOWN IS SAID. `not-in-plan` (the #6608 marker) and
 *      `unavailable` are returned as such; no consumer reads them as "no
 *      workflows".
 *
 * Nothing here says a control is compliant or effective (WF-B-005,
 * TRYGGR_INTENT.md §6.2). A countable run means "the activity happened, inside
 * its window, with at least the evidence its definition expects".
 *
 * A self-reviewed pass is never countable (founder decision D11 = A,
 * 2026-10-06) — that lives in isRunCountable and therefore holds here.
 */

'use strict';

const {
  isRunCountable,
  isBelowExpected,
  scheduleFrequency,
  cadenceWindowMs,
  PERIOD_MONTHS,
} = require('./runAssurance.cjs');
const { planWithheldKeyMap } = require('../compliance/planGateMarker.cjs');

const WORKFLOW_EVIDENCE_KEY = 'workflowEvidence';

const isObj = (v) => !!v && typeof v === 'object' && !Array.isArray(v);
const toMs = (v) =>
  v instanceof Date ? v.getTime() : typeof v === 'number' ? v : typeof v === 'string' ? Date.parse(v) : NaN;
const nowMsOf = (now) => {
  const ms = toMs(now);
  return Number.isFinite(ms) ? ms : Date.now();
};

/**
 * Where the tenant's workflow evidence stands, read from a tenant state.
 *   ok          — the overlay was read; `definitions` is the full list
 *   not-in-plan — the plan withholds workflows (#6608 marker, D8 = B)
 *   unavailable — nothing usable was assembled (an older server, a bad shape)
 *
 * @returns {{ status: 'ok'|'not-in-plan'|'unavailable', definitions: object[] }}
 */
function workflowEvidenceOf(state) {
  const withheld = planWithheldKeyMap(state);
  if (withheld && withheld[WORKFLOW_EVIDENCE_KEY]) {
    // Whether the tenant HAD workflows when the plan withheld them (the
    // marker's withheldNonEmpty, PDWB-03): true / false, or undefined when an
    // older server did not say — then it is named, never assumed absent.
    const marker = state && state.__planGated;
    const said = marker && marker.withheldNonEmpty ? marker.withheldNonEmpty[WORKFLOW_EVIDENCE_KEY] : undefined;
    const hadDefinitions = typeof said === 'boolean' ? said : undefined;
    return { status: 'not-in-plan', definitions: [], hadDefinitions };
  }
  const we = state && state[WORKFLOW_EVIDENCE_KEY];
  if (!isObj(we) || we.status !== 'ok' || !Array.isArray(we.definitions)) {
    return { status: 'unavailable', definitions: [] };
  }
  return { status: 'ok', definitions: we.definitions.filter(isObj) };
}

/** Accepts a tenant state OR an already-resolved evidence object. */
function resolveEvidence(stateOrEvidence) {
  if (isObj(stateOrEvidence) && typeof stateOrEvidence.status === 'string' && Array.isArray(stateOrEvidence.definitions)) {
    return stateOrEvidence.status === 'ok'
      ? { status: 'ok', definitions: stateOrEvidence.definitions.filter(isObj) }
      : { status: stateOrEvidence.status, definitions: [], hadDefinitions: stateOrEvidence.hadDefinitions };
  }
  return workflowEvidenceOf(stateOrEvidence);
}

const linksOf = (def, kind) => (isObj(def.links) && Array.isArray(def.links[kind]) ? def.links[kind] : []);

/**
 * Was the definition published at `nowMs`? The overlay holds each definition's
 * CURRENT head (published or retired); for an instant in the past (the
 * forecast / measurement `asOf`) a definition first published later did not
 * exist yet, and one retired later was still live.
 */
function isLiveAt(def, nowMs) {
  const first = toMs(def.firstPublishedAt);
  if (!Number.isFinite(first) || first > nowMs) return false;
  if (def.status === 'published') return true;
  if (def.status === 'retired') {
    const retired = toMs(def.retiredAt);
    return Number.isFinite(retired) && retired > nowMs;
  }
  return false;
}

/** When `target` first went live as a `kind` link of this definition (ms), or NaN. */
function linkedSinceMs(def, kind, target) {
  const map = isObj(def.linkedSince) && isObj(def.linkedSince[kind]) ? def.linkedSince[kind] : {};
  const ms = toMs(map[target]);
  return Number.isFinite(ms) ? ms : toMs(def.firstPublishedAt);
}

/**
 * The cadence window (ms) a definition's runs are countable in — its own
 * schedule or the shortest linked obligation cadence (scheduleFrequency).
 * Null when nothing recurs: never countable, never overdue (rule 2).
 */
function definitionWindowMs(def, { enabledObligations, obligationsById } = {}) {
  const freq = scheduleFrequency(
    { trigger: def.trigger, links: def.links },
    { enabledObligations: enabledObligations || [], obligationsById: obligationsById || new Map() }
  );
  return cadenceWindowMs(freq);
}

/** The definition's runs attributed to `target` (rule 1), as known at `nowMs`. */
function attributedRuns(def, kind, target, nowMs) {
  return (Array.isArray(def.runs) ? def.runs : []).filter((r) => {
    if (!isObj(r)) return false;
    const snap = isObj(r.definitionSnapshot) && isObj(r.definitionSnapshot.links) ? r.definitionSnapshot.links : {};
    if (!Array.isArray(snap[kind]) || !snap[kind].includes(target)) return false;
    if (r.status === 'running') {
      const created = toMs(r.createdAt);
      return !Number.isFinite(created) || created <= nowMs;
    }
    const done = toMs(r.completedAt);
    return Number.isFinite(done) && done <= nowMs;
  });
}

/** The newest judged (completed — never cancelled) run, or null. */
function latestJudgedRun(runs) {
  return (
    runs
      .filter((r) => r.status === 'completed' && Number.isFinite(toMs(r.completedAt)))
      .sort((a, b) => toMs(b.completedAt) - toMs(a.completedAt))[0] || null
  );
}

/**
 * One definition's standing for one target, at `nowMs`, in window `windowMs`.
 *   { countable: run|null, cause: null|'no-cadence'|'not-yet-run'|'failed'|
 *     'self-reviewed'|'below-expected'|'overdue', openRun: run|null }
 * `cause` is null only when `countable` is set. 'not-yet-run' means the
 * target's first window (from linkedSince) is still open with no judged run.
 */
function standing(def, kind, target, { nowMs, windowMs }) {
  const runs = attributedRuns(def, kind, target, nowMs);
  const openRun = runs.find((r) => r.status === 'running') || null;
  // Unknown is said, never read as absent (rule 3): an unreadable head or run.
  if (def.unreadable === true) return { countable: null, cause: 'unreadable', openRun };
  if (!(Number.isFinite(windowMs) && windowMs > 0)) return { countable: null, cause: 'no-cadence', openRun };
  const latest = latestJudgedRun(runs);
  // The bounded read dropped older runs and none of those read was judged:
  // the latest judged run is unknown, not missing.
  if (!latest && def.runsTruncated === true) return { countable: null, cause: 'unreadable', openRun };
  if (latest && isRunCountable(latest, { now: nowMs, windowMs })) return { countable: latest, cause: null, openRun };
  const since = linkedSinceMs(def, kind, target);
  const firstWindowOpen = Number.isFinite(since) && since > nowMs - windowMs;
  let cause;
  if (!latest) cause = firstWindowOpen ? 'not-yet-run' : 'overdue';
  else if (latest.outcome !== 'pass') cause = 'failed';
  else if (latest.selfReviewed === true) cause = 'self-reviewed';
  else if (isBelowExpected(latest)) cause = 'below-expected';
  else cause = firstWindowOpen ? 'not-yet-run' : 'overdue';
  return { countable: null, cause, openRun };
}

const CAUSE_LABEL = {
  unreadable: 'its record could not be read',
  'no-cadence': 'no schedule, so no run is ever current',
  'not-yet-run': 'first run not yet due',
  failed: 'latest run failed',
  'self-reviewed': 'latest pass rests on a self-reviewed step',
  'below-expected': 'latest run below expected evidence strength',
  overdue: 'overdue',
};

// ── Gate 4 (evaluateControl) ────────────────────────────────────────────────

/**
 * Gate 4's workflow evidence for one control (design §3g, §3n): every
 * definition live at `now` whose current links.controls holds the control,
 * split into those whose latest judged run attributed to the control is
 * countable in the definition's window, and those without (named with why).
 *
 * @param {object} stateOrEvidence  tenant state, or workflowEvidenceOf(state)
 * @param {(id: string) => boolean} matchesControl  the caller's TCF matcher
 * @returns {{ status: string, countable: object[], notCounted: object[] }}
 */
function controlWorkflowEvidence(stateOrEvidence, matchesControl, { now, enabledObligations, obligationsById } = {}) {
  const { status, definitions, hadDefinitions } = resolveEvidence(stateOrEvidence);
  const countable = [];
  const notCounted = [];
  if (status !== 'ok') return { status, countable, notCounted, hadDefinitions };
  const nowMs = nowMsOf(now);
  for (const def of definitions) {
    if (!isLiveAt(def, nowMs)) continue;
    for (const target of linksOf(def, 'controls').filter(matchesControl)) {
      const windowMs = definitionWindowMs(def, { enabledObligations, obligationsById });
      const st = standing(def, 'controls', target, { nowMs, windowMs });
      if (st.countable) {
        countable.push({
          definitionId: def.definitionId,
          title: def.title,
          runId: st.countable.runId,
          completedAt: st.countable.completedAt,
          achievedStrength: st.countable.achievedStrength,
        });
      } else {
        notCounted.push({ definitionId: def.definitionId, title: def.title, cause: st.cause, label: CAUSE_LABEL[st.cause] });
      }
      break; // one entry per definition, whichever of its control ids matched
    }
  }
  return { status, countable, notCounted };
}

// ── Obligations: `workflow-run-current` and the task annotation ─────────────

/**
 * The obligation's effective cadence window (a tenant cadenceOverride wins),
 * or null when it has none.
 */
function obligationWindowMs(obligationId, { enabledObligations, obligationsById } = {}) {
  const eo = (enabledObligations || []).find((e) => e && e.obligationId === obligationId);
  const override = eo && eo.cadenceOverride && eo.cadenceOverride.frequency;
  const def = obligationsById && typeof obligationsById.get === 'function' ? obligationsById.get(obligationId) : null;
  const freq = override || (def && def.cadence && def.cadence.frequency) || null;
  return PERIOD_MONTHS[freq] ? cadenceWindowMs(freq) : null;
}

/** Definitions live at `nowMs` whose current version links the obligation. */
function obligationDefinitions(definitions, obligationId, nowMs) {
  return definitions.filter((def) => isLiveAt(def, nowMs) && linksOf(def, 'obligations').includes(obligationId));
}

/**
 * The `workflow-run-current` criterion for one obligation (design §3e), judged
 * in the OBLIGATION's own window for every definition (co-owner item 3):
 *   pass    — some linked definition with a cadence has a countable run
 *             (attributed to this obligation) inside the obligation's window
 *   fail    — otherwise, when some linked definition with a cadence is past
 *             the obligation's first window since it linked the obligation
 *   null    — no verdict yet (every linked definition is inside its first
 *             window), or no linked definition has a cadence: the criterion
 *             is not appended and the obligation's own criteria decide
 * Returns null when the evidence is not `ok` — see obligationEvaluator, which
 * never lets that read as a pass.
 *
 * @returns {null | { pass: boolean, summary: string, details: object }}
 */
function obligationWorkflowCriterion(stateOrEvidence, obligationId, { now, enabledObligations, obligationsById } = {}) {
  const { status, definitions } = resolveEvidence(stateOrEvidence);
  if (status !== 'ok') return null;
  const nowMs = nowMsOf(now);
  const windowMs = obligationWindowMs(obligationId, { enabledObligations, obligationsById });
  if (!windowMs) return null;
  // An unreadable definition has no readable trigger, so no window — it is
  // kept anyway (co-owner round 3): dropping it would drop the criterion of
  // an 'all' / manual obligation whose workflow is in an unknown state.
  const linked = obligationDefinitions(definitions, obligationId, nowMs).filter(
    (def) => def.unreadable === true || definitionWindowMs(def, { enabledObligations, obligationsById }) !== null
  );
  if (!linked.length) return null;
  const rows = linked.map((def) => ({ def, st: standing(def, 'obligations', obligationId, { nowMs, windowMs }) }));
  const passing = rows.find((r) => r.st.countable);
  const detail = rows.map(({ def, st }) => ({
    definitionId: def.definitionId,
    title: def.title,
    runId: st.countable ? st.countable.runId : null,
    completedAt: st.countable ? st.countable.completedAt : null,
    achievedStrength: st.countable ? st.countable.achievedStrength : null,
    cause: st.cause,
  }));
  if (passing) {
    const run = passing.st.countable;
    return {
      pass: true,
      summary: `Workflow "${passing.def.title}" ran inside this obligation's window — run completed pass on ${String(run.completedAt).slice(0, 10)}, evidence strength ${run.achievedStrength}`,
      details: { definitions: detail },
    };
  }
  const unknown = rows.filter((r) => r.st.cause === 'unreadable');
  if (unknown.length) {
    // Not known is neither met nor failed: pass null keeps an 'all' / manual
    // obligation from reading fulfilled, without asserting a failure.
    return {
      pass: null,
      summary: `The linked workflow could not be read — ${unknown.map((r) => `"${r.def.title || r.def.definitionId}"`).join(', ')}`,
      details: { definitions: detail },
    };
  }
  const late = rows.filter((r) => r.st.cause !== 'not-yet-run');
  if (!late.length) return null;
  return {
    pass: false,
    summary: `No countable run of the linked workflow inside this obligation's window — ${late
      .map((r) => `"${r.def.title}": ${CAUSE_LABEL[r.st.cause]}`)
      .join('; ')}`,
    details: { definitions: detail },
  };
}

/**
 * Who is doing an obligation's work through a workflow (design §3e "no double
 * work", PDWB-03 co-owner item 4). The obligation stays in every count; its
 * task is ANNOTATED, never dropped:
 *   run-open  — a run attributed to this obligation is open: its steps are the
 *               work, the task points at the run
 *   start-run — no run open and a linked MANUAL definition exists: "Start run"
 *   scheduled — no run open, the linked definition runs on its schedule (no
 *               dead "Start run" button: workflow-steps-write refuses a manual
 *               start of a scheduled definition)
 * Null when the evidence is not `ok` (never a Start-run on a not-in-plan
 * tenant) or no live definition links the obligation.
 */
function obligationWorkflowAnnotation(stateOrEvidence, obligationId, { now } = {}) {
  const { status, definitions } = resolveEvidence(stateOrEvidence);
  if (status !== 'ok') return null;
  const nowMs = nowMsOf(now);
  const linked = obligationDefinitions(definitions, obligationId, nowMs).filter((d) => d.status === 'published' && d.unreadable !== true);
  if (!linked.length) return null;
  for (const def of linked) {
    const open = attributedRuns(def, 'obligations', obligationId, nowMs).find((r) => r.status === 'running');
    if (open) {
      return { state: 'run-open', definitionId: def.definitionId, title: def.title, runId: open.runId, ownerRole: def.ownerRole || '' };
    }
  }
  const manual = linked.find((d) => d.trigger && d.trigger.type === 'manual');
  if (manual) {
    return { state: 'start-run', definitionId: manual.definitionId, title: manual.title, ownerRole: manual.ownerRole || '' };
  }
  const def = linked[0];
  const frequency = def.trigger && def.trigger.type === 'schedule' ? def.trigger.frequency || null : null;
  return { state: 'scheduled', definitionId: def.definitionId, title: def.title, frequency, ownerRole: def.ownerRole || '' };
}

// ── Risks: workflow backing of a treatment decision (RISK-B-006, D1 = A) ────

/**
 * The definitions that may back a treatment of `riskId` at activation:
 * published now, with a cadence (co-owner item 5a — a definition that never
 * has to run can never be "owed work"), whose current version links the risk.
 */
function riskBackingCandidates(stateOrEvidence, riskId, { now, enabledObligations, obligationsById } = {}) {
  const { status, definitions } = resolveEvidence(stateOrEvidence);
  if (status !== 'ok') return [];
  const nowMs = nowMsOf(now);
  return definitions.filter(
    (def) =>
      def.status === 'published' &&
      def.unreadable !== true &&
      isLiveAt(def, nowMs) &&
      linksOf(def, 'risks').includes(riskId) &&
      definitionWindowMs(def, { enabledObligations, obligationsById }) !== null
  );
}

/**
 * Why the workflows recorded as backing a decision (decision.workflowBacking,
 * set at activation) no longer back it. Empty when they all still do.
 * Causes: retired, unlinked (no longer links the risk), no-cadence, failed /
 * self-reviewed / below-expected (latest judged run not countable), overdue
 * (no countable run inside the window, measured from activation) — and
 * `unreadable`, which is UNKNOWN, not a lapse: isDecisionValid reports it as
 * workflow-unverifiable (the sweeper never reopens a risk on it).
 *
 * @param {{ status: string, definitions: object[] }} evidence  resolved
 * @returns {{ definitionId: string, cause: string }[]}
 */
function lapsedWorkflowBacking(evidence, decision, riskId, { now, enabledObligations, obligationsById } = {}) {
  const ids = Array.isArray(decision && decision.workflowBacking) ? decision.workflowBacking : [];
  const byId = new Map((evidence.definitions || []).map((d) => [d.definitionId, d]));
  const nowMs = nowMsOf(now);
  const anchor = toMs(decision.activatedAt || decision.assertedAt);
  const out = [];
  for (const id of ids) {
    const def = byId.get(id);
    if (!def || def.status !== 'published') {
      out.push({ definitionId: id, cause: 'retired' });
      continue;
    }
    if (!linksOf(def, 'risks').includes(riskId)) {
      out.push({ definitionId: id, cause: 'unlinked' });
      continue;
    }
    const windowMs = definitionWindowMs(def, { enabledObligations, obligationsById });
    const st = standing(def, 'risks', riskId, { nowMs, windowMs });
    if (st.countable) continue;
    if (st.cause === 'overdue' || st.cause === 'not-yet-run') {
      // The obligation to run starts at activation: inside the first window
      // after it, a decision is not yet owed a run (the riskObligations rule,
      // anchored on the decision).
      if (Number.isFinite(anchor) && Number.isFinite(windowMs) && anchor > nowMs - windowMs) continue;
      out.push({ definitionId: id, cause: 'overdue' });
      continue;
    }
    out.push({ definitionId: id, cause: st.cause });
  }
  return out;
}

module.exports = {
  WORKFLOW_EVIDENCE_KEY,
  CAUSE_LABEL,
  workflowEvidenceOf,
  resolveEvidence,
  definitionWindowMs,
  obligationWindowMs,
  isLiveAt,
  controlWorkflowEvidence,
  obligationWorkflowCriterion,
  obligationWorkflowAnnotation,
  riskBackingCandidates,
  lapsedWorkflowBacking,
};
